/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, Card, CardContent, Stack, TextField, Typography } from "@mui/material";
import { FC, useCallback, useMemo } from "react";
import { Controller, Path, useFormContext } from "react-hook-form";

import { Campaign, Lookup, LookupValue } from "../../api/types/types";

import CompetitorAutocomplete from "./CompetitorAutocomplete";
import { buildCampaignFieldPath, CAMPAIGN_FIELD_NAMES } from "./constants";
import LookupSelect, { findLookupValue, getLookupValueString } from "./LookupSelect";
import { CampaignFormData } from "./types";
import { isNotesRequired } from "./validationUtils";

export interface CampaignFormCardProps {
  campaign: Campaign;
  campaignIndex: number;
  campaignKey: string;
  responseCodeLookup: Lookup | undefined;
  reasonLookup: Lookup | undefined;
  typeLookup: Lookup | undefined;
  competitorLookup: Lookup | undefined;
  sortedCompetitorOptions: LookupValue[];
  showCompetitor: boolean;
  hasReasons: boolean;
  hasTypes: boolean;
  onResponseCodeChange: (value: string) => void;
}

const CampaignFormCard: FC<CampaignFormCardProps> = ({
  campaign,
  campaignIndex,
  campaignKey,
  responseCodeLookup,
  reasonLookup,
  typeLookup,
  competitorLookup,
  sortedCompetitorOptions,
  showCompetitor,
  hasReasons,
  hasTypes,
  onResponseCodeChange,
}) => {
  type FormData = Record<string, CampaignFormData>;

  const getFieldValue = (value: unknown): string => {
    return (typeof value === "string" ? value : "") || "";
  };

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<FormData>();
  const campaignData = watch(campaignKey) || {
    responseCode: "",
    reason: "",
    type: "",
    competitor: "",
    notes: "",
  };
  const isResponseCodeSelected = !!campaignData.responseCode;
  const notesRequired = isNotesRequired(campaignData.responseCode);
  const notesError = errors[campaignKey]?.notes;
  const reasonError = errors[campaignKey]?.reason;
  const typeError = errors[campaignKey]?.type;
  const competitorError = errors[campaignKey]?.competitor;

  const filteredResponseCodeLookup = useMemo(() => {
    if (!responseCodeLookup) {
      return undefined;
    }
    return {
      ...responseCodeLookup,
      values: responseCodeLookup.values.filter((v) => v.label !== "Open"),
    };
  }, [responseCodeLookup]);

  const getCompetitorValue = useCallback(
    (competitorString: string): LookupValue | null => {
      if (!competitorString) {
        return null;
      }
      return findLookupValue(competitorLookup, competitorString) || null;
    },
    [competitorLookup]
  );

  const competitorValue = useMemo(
    () => getCompetitorValue(campaignData.competitor),
    [campaignData.competitor, getCompetitorValue]
  );

  return (
    <Card key={campaign.campaignId || campaignIndex} variant="outlined">
      <CardContent>
        <Stack gap={2}>
          <Typography variant="h6" fontWeight={600} sx={{ color: colors.shade100 }}>
            {campaign.campaignName}
          </Typography>
          <Controller<FormData, Path<FormData>>
            name={buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.RESPONSE_CODE)}
            control={control}
            render={({ field }) => (
              <LookupSelect
                label="Response Code (Optional)"
                value={getFieldValue(field.value)}
                lookup={filteredResponseCodeLookup}
                campaignKey={campaignKey}
                placeholder="Select response code"
                onChange={(value) => {
                  field.onChange(value);
                  onResponseCodeChange(value);
                }}
              />
            )}
          />
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            {hasReasons && (
              <Controller<FormData, Path<FormData>>
                name={buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.REASON)}
                control={control}
                render={({ field }) => (
                  <LookupSelect
                    label="Reason"
                    value={getFieldValue(field.value)}
                    lookup={reasonLookup}
                    campaignKey={campaignKey}
                    placeholder="Select reason"
                    disabled={!isResponseCodeSelected}
                    onChange={field.onChange}
                    error={!!reasonError}
                  />
                )}
              />
            )}
            {hasTypes && (
              <Controller<FormData, Path<FormData>>
                name={buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.TYPE)}
                control={control}
                render={({ field }) => (
                  <LookupSelect
                    label="Type"
                    value={getFieldValue(field.value)}
                    lookup={typeLookup}
                    campaignKey={campaignKey}
                    placeholder="Select type"
                    disabled={!isResponseCodeSelected}
                    onChange={field.onChange}
                    error={!!typeError}
                  />
                )}
              />
            )}
          </Stack>
          {showCompetitor && (
            <Controller
              name={buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.COMPETITOR)}
              control={control}
              render={({ field }) => (
                <CompetitorAutocomplete
                  value={competitorValue}
                  options={sortedCompetitorOptions}
                  disabled={!isResponseCodeSelected}
                  onChange={(value) => {
                    const stringValue = value ? getLookupValueString(value) : "";
                    field.onChange(stringValue);
                  }}
                  error={!!competitorError}
                />
              )}
            />
          )}
          <Controller
            name={buildCampaignFieldPath(campaignKey, CAMPAIGN_FIELD_NAMES.NOTES)}
            control={control}
            render={({ field }) => (
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  Notes{notesRequired ? "" : " (Optional)"}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  disabled={!isResponseCodeSelected}
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder="Add notes..."
                  error={!!notesError}
                  sx={{
                    "& .MuiInputBase-root.Mui-disabled": {
                      backgroundColor: colors.primary50,
                    },
                  }}
                />
              </Box>
            )}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CampaignFormCard;
